import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronRight,
  FilePlus2,
  FileSignature,
  Gavel,
  History,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Upload,
  Archive,
} from "lucide-react";
import apiClient from "@/api/client";

/**
 * Oś czasu jednej umowy ramowej — kto ją założył, co zmieniał, jakie pliki wgrywał
 * i co robił z cennikiem.
 *
 * Wartości pól tu nie ma (poza nazwami pozycji): backend loguje same etykiety
 * zmienionych pól, żeby historia nie stała się obejściem bramki na stawki.
 * Umowa skasowana nie ma już swojej strony — jej ostatni wpis żyje na karcie firmy
 * (ContractorHistorySection).
 */

interface HistoryEntry {
  id: string;
  verb: string;
  description: string;
  timestamp: string;
  actor_name: string | null;
  metadata: Record<string, unknown>;
}

const VERBS: Record<string, { label: string; Icon: typeof History; color: string }> = {
  AGREEMENT_CREATED: {
    label: "Założenie umowy",
    Icon: FileSignature,
    color: "text-green-600 dark:text-green-400",
  },
  AGREEMENT_UPDATED: {
    label: "Zmiana danych umowy",
    Icon: Pencil,
    color: "text-[var(--color-text-muted)]",
  },
  AGREEMENT_FILE_UPLOADED: {
    label: "Wgranie pliku",
    Icon: Upload,
    color: "text-blue-600 dark:text-blue-400",
  },
  AGREEMENT_EXTRA_FILE_STATUS_CHANGED: {
    label: "Zmiana oznaczenia pliku",
    Icon: Pencil,
    color: "text-[var(--color-text-muted)]",
  },
  AGREEMENT_EXTRA_FILE_DELETED: {
    label: "Usunięcie innego pliku umowy",
    Icon: Trash2,
    color: "text-red-600 dark:text-red-400",
  },
  AGREEMENT_DELETED: {
    label: "Usunięcie umowy",
    Icon: Trash2,
    color: "text-red-600 dark:text-red-400",
  },
  AGREEMENT_LINE_ITEM_ADDED: {
    label: "Nowa pozycja cennika",
    Icon: Plus,
    color: "text-green-600 dark:text-green-400",
  },
  AGREEMENT_LINE_ITEM_UPDATED: {
    label: "Zmiana pozycji cennika",
    Icon: FilePlus2,
    color: "text-[var(--color-text-muted)]",
  },
  AGREEMENT_LINE_ITEM_DELETED: {
    label: "Usunięcie pozycji cennika",
    Icon: Trash2,
    color: "text-red-600 dark:text-red-400",
  },
  AGREEMENT_LINE_ITEM_WITHDRAWN: {
    label: "Wycofanie pozycji cennika",
    Icon: Archive,
    color: "text-amber-600 dark:text-amber-500",
  },
  AGREEMENT_LINE_ITEM_RESTORED: {
    label: "Przywrócenie pozycji cennika",
    Icon: RotateCcw,
    color: "text-[var(--color-primary)]",
  },
  AGREEMENT_PENALTY_TERM_ADDED: {
    label: "Nowy warunek kary umownej",
    Icon: Gavel,
    color: "text-green-600 dark:text-green-400",
  },
  AGREEMENT_PENALTY_TERM_UPDATED: {
    label: "Zmiana warunku kary umownej",
    Icon: Gavel,
    color: "text-[var(--color-text-muted)]",
  },
  AGREEMENT_PENALTY_TERM_DELETED: {
    label: "Usunięcie warunku kary umownej",
    Icon: Gavel,
    color: "text-red-600 dark:text-red-400",
  },
};

const INITIAL_VISIBLE = 8;

export function AgreementHistorySection({ agreementId }: { agreementId: string }) {
  const [expanded, setExpanded] = useState(false);

  const { data: entries } = useQuery({
    queryKey: ["agreement", agreementId, "history"],
    queryFn: () =>
      apiClient
        .get<{ data: HistoryEntry[] }>(`/agreements/${agreementId}/history/`)
        .then((r) => r.data.data),
  });

  if (!entries) return null;

  const visible = expanded ? entries : entries.slice(0, INITIAL_VISIBLE);

  return (
    <section className="mb-6">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-200">
        <History size={15} className="text-gray-400 dark:text-gray-500" />
        Historia umowy
        <span className="font-normal text-[var(--color-text-muted)]">
          ({entries.length})
        </span>
      </h3>

      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        {entries.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">
            Brak zdarzeń — umowa nie była zmieniana, odkąd zbieramy historię.
          </p>
        ) : (
          <>
            <ol className="relative space-y-4 border-l border-[var(--color-border)] pl-5">
              {visible.map((entry) => {
                const config = VERBS[entry.verb];
                const Icon = config?.Icon ?? History;
                return (
                  <li key={entry.id} className="relative">
                    <span
                      className={`absolute -left-[26px] flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white dark:bg-gray-800 ${config?.color ?? ""}`}
                    >
                      <Icon size={12} />
                    </span>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-sm font-medium text-[var(--color-text-primary)]">
                        {config?.label ?? entry.verb}
                      </span>
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {new Date(entry.timestamp).toLocaleString("pl")}
                        {entry.actor_name && ` · ${entry.actor_name}`}
                      </span>
                    </div>
                    {entry.description && (
                      <p className="text-xs text-[var(--color-text-secondary)]">
                        {entry.description}
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>

            {entries.length > INITIAL_VISIBLE && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-4 inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
              >
                {expanded ? (
                  <>
                    <ChevronDown size={12} /> Pokaż mniej
                  </>
                ) : (
                  <>
                    <ChevronRight size={12} /> Pokaż wszystkie ({entries.length})
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
